import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import clsx from "clsx";
import { FieldShell } from "../Field/FieldShell.js";
import type { FieldLabelVariant } from "../Field/Field.js";
import fieldStyles from "../Field/Field.module.scss";
import { useDismiss } from "../useDismiss/useDismiss.js";
import { usePickerSheet } from "../hooks/usePickerSheet.js";
import { Sheet } from "../Sheet/Sheet.js";
import styles from "./Picker.module.scss";

/** One choice. `label` is PLAIN TEXT — it feeds the type-ahead filter and the accessible name; `render` is the
 * glyph-capable row in both the web listbox and the touch sheet (defaults to the label). */
export interface PickerOption {
  value: string;
  label: string;
  render?: ReactNode;
  /** Optional group heading (the LinksCard platform families). Groups render as headers on the web
   * listbox and the touch sheet; options are listed in given order within
   * the order groups first appear. */
  group?: string;
  disabled?: boolean;
}

export interface PickerProps {
  /** Accessible name. Rendered by the FieldShell frame, or applied as `aria-label` when `bare`. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: PickerOption[];
  /**
   * `bare` drops the label/hint/error frame entirely — for dense rows that already name the control
   * (the assets tables). The label still reaches AT via `aria-label`.
   */
  bare?: boolean;
  /** Keep the label frame's BOX but read the label to screen readers only (`Select`'s `hideLabel`) — for a row
   *  that names the control visibly. Unlike `bare`, the trigger keeps the house field treatment. */
  hideLabel?: boolean;
  disabled?: boolean;
  /** The trigger becomes a TYPE-AHEAD filter input styled like `Field`. On a touch device the trigger stays a
   * box and the filter moves into a search field at the top of the sheet. */
  typeahead?: boolean;
  /**
   * Fully custom trigger content (the profile modal's role chip). The Picker renders it inside an
   * unstyled button — the caller styles the content, the Picker owns the interaction (listbox on web,
   * sheet on touch).
   */
  renderTrigger?: (state: { selected: PickerOption | undefined; open: boolean }) => ReactNode;
  hint?: ReactNode;
  error?: string;
  subLabel?: ReactNode;
  labelVariant?: FieldLabelVariant;
  placeholder?: string;
  className?: string;
  /** Extra class on the trigger control (dense/compact variants at the call site). */
  triggerClassName?: string;
  /**
   * Put the caret in the control as soon as it mounts. For a picker REVEALED by a press ("+ Add") — where
   * landing in the field is the whole point of the press — not for one that is simply on the page. It is an
   * effect rather than the `autoFocus` prop deliberately: that prop also fires on page load, which is what
   * the a11y rule bans it for.
   */
  focusOnMount?: boolean;
  id?: string;
}

// ---- the component -----------------------------------------------------------------------------

/**
 * The house select. One control, three trigger shapes (box / type-ahead / fully custom via
 * `renderTrigger`), and two popup implementations chosen by DEVICE, not viewport (`usePickerSheet`):
 *
 * - **Web (fine pointer):** a custom `role="listbox"` popup — the nav-dropdown chrome (paper,
 *   line border, radius-md, shadow) with select-box spacing — with the full combobox keyboard
 *   contract (arrows, Home/End, type-to-jump, Enter/Space, Escape). Focus never leaves the trigger
 *   (`aria-activedescendant`), which is also what keeps the Modal focus trap oblivious to it.
 * - **Coarse pointer:** a bottom `Sheet` of full-width, 44px+ rows (ART-266) — it REPLACES the native
 *   `<select>` this used to fall back to. The system wheel could only draw plain text (no AV mark, no
 *   avatar, no artwork), had no search for a long list, and ignored the app's dark theme. The trigger is
 *   the same one the web path draws; only the popup differs. Non-production lanes can force the sheet on
 *   a narrow desktop window (`setPickerSheetPreview`) so it can be reviewed without a phone.
 *
 * The popup PORTALS to `document.body` (fixed-position, above the modal layer): triggers live inside
 * clipped containers (the profile hero's `overflow: hidden`, the modal's corner clip), so an inline
 * absolute popup would be cut off. While open, a CAPTURE-phase Escape closes ONLY the picker — the
 * Modal's Escape listener (bubble, document) never sees it, so a dropdown inside a dialog unwinds
 * one layer at a time like the overlay stack does.
 */
export function Picker({
  label,
  value,
  onChange,
  options,
  bare = false,
  hideLabel = false,
  disabled = false,
  typeahead = false,
  renderTrigger,
  hint,
  error,
  subLabel,
  labelVariant,
  placeholder,
  className,
  triggerClassName,
  focusOnMount = false,
  id,
}: PickerProps) {
  const autoId = useId();
  const baseId = id ?? autoId;
  const listboxId = `${baseId}-listbox`;
  const sheetMode = usePickerSheet();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null); // typeahead filter; null = not filtering
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (focusOnMount) triggerRef.current?.focus();
  }, [focusOnMount]);
  const listRef = useRef<HTMLDivElement>(null);
  const typeBuffer = useRef({ text: "", at: 0 });

  const selected = options.find((o) => o.value === value);

  // The visible list: filtered in typeahead mode, verbatim otherwise. Group order = first appearance.
  const visible = useMemo(() => {
    if (query == null || query === "") return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery(null);
    setActive(-1);
  }, []);

  const openList = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    const idx = options.findIndex((o) => o.value === value && !o.disabled);
    setActive(idx >= 0 ? idx : options.findIndex((o) => !o.disabled));
  }, [disabled, options, value]);

  const pick = useCallback(
    (opt: PickerOption) => {
      if (opt.disabled) return;
      onChange(opt.value);
      close();
      triggerRef.current?.focus();
    },
    [onChange, close],
  );

  // The sheet owns its own dismissal (scrim, drag, Escape via the overlay stack); the light outside-press
  // dismiss would read every tap inside the portalled sheet as "outside" and close it before the pick lands.
  useDismiss(open && !sheetMode, close, rootRef);

  // Escape, CAPTURE phase, only while open: closes the picker and stops the event cold, so a Modal's
  // document-level (bubble) Escape handler behind it never fires. The overlay-stack rule, one layer down.
  useEffect(() => {
    if (!open || sheetMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      close();
      triggerRef.current?.focus();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, close, sheetMode]);

  // Fixed-position anchor tracking for the portaled popup: measured on open, re-measured on any
  // scroll/resize (capture catches inner scrollers). Flips upward when the space below is tighter.
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number; up: boolean } | null>(null);
  useEffect(() => {
    if (!open || sheetMode) {
      setAnchor(null);
      return;
    }
    const measure = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const up = spaceBelow < 280 && r.top > spaceBelow;
      setAnchor({ top: up ? r.top - 6 : r.bottom + 6, left: r.left, width: r.width, up });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, sheetMode]);

  // Keep the active option in view as the arrows move it. (Feature-tested: jsdom has no scrollIntoView.)
  useEffect(() => {
    if (!open || active < 0) return;
    const el = listRef.current?.querySelector(`[data-index="${active}"]`);
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  // The touch sheet opens on the CURRENT choice, as the native wheel did — a long list otherwise opens at the top
  // with the selected row out of sight.
  useEffect(() => {
    if (!open || !sheetMode) return;
    const el = document.getElementById(listboxId)?.querySelector('[aria-selected="true"]');
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center" });
  }, [open, sheetMode, listboxId]);

  const step = useCallback(
    (from: number, dir: 1 | -1): number => {
      let i = from;
      for (let n = 0; n < visible.length; n++) {
        i += dir;
        if (i < 0 || i >= visible.length) return from;
        if (!visible[i]?.disabled) return i;
      }
      return from;
    },
    [visible],
  );

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp": {
        e.preventDefault();
        if (!open) {
          openList();
          return;
        }
        setActive((a) => step(a, e.key === "ArrowDown" ? 1 : -1));
        return;
      }
      case "Home":
      case "End": {
        if (!open) return;
        e.preventDefault();
        const first = visible.findIndex((o) => !o.disabled);
        const last = visible.length - 1 - [...visible].reverse().findIndex((o) => !o.disabled);
        setActive(e.key === "Home" ? first : last);
        return;
      }
      case "Enter":
      case " ": {
        // Space types into the typeahead input rather than committing.
        if (typeahead && e.key === " ") return;
        if (!open) {
          e.preventDefault();
          openList();
          return;
        }
        e.preventDefault();
        const opt = visible[active];
        if (opt) pick(opt);
        return;
      }
      case "Tab": {
        if (open) close();
        return;
      }
      default: {
        // Closed-or-open type-to-jump for the non-filtering modes: accumulate a short buffer and jump
        // to the first label with that prefix (the native select behaviour users already have).
        if (typeahead || e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return;
        const now = Date.now();
        const buf = typeBuffer.current;
        buf.text = (now - buf.at < 600 ? buf.text : "") + e.key.toLowerCase();
        buf.at = now;
        const hit = options.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(buf.text));
        if (hit < 0) return;
        if (open) setActive(hit);
        else onChange(options[hit].value);
      }
    }
  };

  // ---- web path: custom trigger + portaled listbox ----------------------------------------------

  const comboProps = {
    role: "combobox" as const,
    "aria-expanded": open,
    "aria-haspopup": "listbox" as const,
    "aria-controls": open ? listboxId : undefined,
    "aria-activedescendant": open && !sheetMode && active >= 0 ? `${baseId}-opt-${active}` : undefined,
    onKeyDown,
  };

  const sheet =
    open && sheetMode ? (
      <Sheet title={label} onClose={() => close()} scroll="inner" stacked>
        <div className={styles.sheetBody}>
          <p className={styles.sheetTitle} aria-hidden="true">
            {label}
          </p>
          {typeahead && (
            <input
              type="search"
              className={clsx(fieldStyles.control, styles.sheetSearch)}
              value={query ?? ""}
              placeholder="Search"
              aria-label={`Search ${label}`}
              autoComplete="off"
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <div id={listboxId} role="listbox" aria-label={label} className={styles.sheetList}>
            {renderSheetOptions(visible, { value, pick })}
            {visible.length === 0 && <p className={styles.emptyNote}>No matches.</p>}
          </div>
        </div>
      </Sheet>
    ) : null;

  const listbox = sheet ??
    (open && anchor
      ? createPortal(
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label={label}
            className={styles.listbox}
            style={{
              top: anchor.up ? undefined : anchor.top,
              bottom: anchor.up ? window.innerHeight - anchor.top : undefined,
              left: anchor.left,
              minWidth: Math.max(anchor.width, 180),
            }}
            // pointerdown inside must not read as an outside-dismiss (the portal is not a DOM child of
            // rootRef, so useDismiss would otherwise close the list before click fires on the option) —
            // and it must not STEAL FOCUS either: in typeahead mode the input's blur resets the filter,
            // which would repaint the full list under the pointer mid-click and land the click on the
            // wrong option (caught live, 2026-08-20). preventDefault keeps focus where it is.
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {renderWebOptions(visible, { baseId, active, value, pick, setActive })}
            {visible.length === 0 && <p className={styles.emptyNote}>No matches.</p>}
          </div>,
          document.body,
        )
      : null);

  if (renderTrigger) {
    const chip = (
      <div ref={rootRef} className={clsx(styles.chipWrap, className)}>
        <button
          type="button"
          ref={(el) => void (triggerRef.current = el)}
          className={clsx(styles.chipTrigger, triggerClassName)}
          disabled={disabled}
          aria-label={label}
          {...comboProps}
          onClick={() => (open ? close() : openList())}
        >
          {renderTrigger({ selected, open })}
        </button>
        {listbox}
      </div>
    );
    return chip;
  }

  // On touch the type-ahead's filter lives in the sheet, so its trigger is the plain box below.
  if (typeahead && !sheetMode) {
    const inputShown = query ?? selected?.label ?? "";
    const control = (props: { id?: string; className?: string; [k: string]: unknown }) => (
      <div ref={rootRef} className={styles.anchor}>
        <input
          {...props}
          ref={(el) => void (triggerRef.current = el)}
          type="text"
          value={inputShown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-label={bare ? label : undefined}
          {...comboProps}
          onFocus={() => openList()}
          // Focus alone is not enough for a picker used as an ADD TRIGGER (`ChipPicker`): picking refocuses
          // the input, so after the first choice the field is focused-and-closed and a click fires no
          // `focus` event — the list would only reopen if you typed. Clicking a CLOSED typeahead reopens it;
          // clicking an open one is left alone, so moving the caret inside the text does not dismiss the list.
          onClick={() => {
            if (!open) openList();
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
            setActive(0);
          }}
          onBlur={() => setQuery(null)}
        />
        {listbox}
      </div>
    );
    if (bare) return control({ className: clsx(fieldStyles.control, styles.bareControl, triggerClassName) });
    return (
      <FieldShell label={label} hideLabel={hideLabel} labelVariant={labelVariant} subLabel={subLabel} error={error} hint={hint} className={className}>
        {(props) => control({ ...props, className: clsx(props.className, triggerClassName) })}
      </FieldShell>
    );
  }

  // Default box trigger: the Select look on a button. `fieldStyles.select` only RESERVES arrow room
  // (the native control drew its own); a button draws nothing, so the chevron is ours.
  // In `bare` mode the caller's `triggerClassName` owns the whole treatment (the dense assets rows
  // carry complete local select styles) — stacking the Field control there would double the chrome.
  const boxButton = (props: { id?: string; className?: string; [k: string]: unknown }) => (
    <div ref={rootRef} className={clsx(styles.anchor, bare && styles.anchorInline)}>
      <button
        type="button"
        {...props}
        ref={(el) => void (triggerRef.current = el)}
        className={clsx(
          props.className,
          bare ? styles.bareControl : fieldStyles.select,
          styles.boxTrigger,
          triggerClassName,
        )}
        disabled={disabled}
        aria-label={bare ? label : undefined}
        {...comboProps}
        onClick={() => (open ? close() : openList())}
      >
        <span className={styles.boxValue} data-empty={selected ? undefined : ""}>
          {selected ? (selected.render ?? selected.label) : (placeholder ?? "—")}
        </span>
        <ChevronDown size={16} className={styles.boxChevron} aria-hidden="true" />
      </button>
      {listbox}
    </div>
  );
  if (bare) return boxButton({ className: undefined });
  return (
    <FieldShell label={label} hideLabel={hideLabel} labelVariant={labelVariant} subLabel={subLabel} error={error} hint={hint} className={className}>
      {(props) => boxButton(props)}
    </FieldShell>
  );
}

// ---- shared render helpers ---------------------------------------------------------------------

function renderWebOptions(
  visible: PickerOption[],
  ctx: {
    baseId: string;
    active: number;
    value: string;
    pick: (o: PickerOption) => void;
    setActive: (i: number) => void;
  },
) {
  let lastGroup: string | undefined;
  return visible.map((o, i) => {
    const header =
      o.group !== lastGroup && o.group != null ? (
        <p key={`g-${o.group}`} className={styles.groupHeader} role="presentation">
          {o.group}
        </p>
      ) : null;
    lastGroup = o.group;
    return (
      <span key={o.value}>
        {header}
        <div
          id={`${ctx.baseId}-opt-${i}`}
          data-index={i}
          role="option"
          // Programmatically focusable only — DOM focus stays on the combobox (aria-activedescendant
          // is the keyboard model); this satisfies the interactive-role contract without stealing focus.
          tabIndex={-1}
          aria-selected={o.value === ctx.value}
          aria-disabled={o.disabled || undefined}
          className={clsx(
            styles.option,
            i === ctx.active && styles.optionActive,
            o.disabled && styles.optionDisabled,
          )}
          onPointerEnter={() => !o.disabled && ctx.setActive(i)}
          onClick={() => ctx.pick(o)}
          // Ordinarily unreachable (focus lives on the combobox), but if focus is ever placed here
          // programmatically, the option still commits from the keyboard.
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              ctx.pick(o);
            }
          }}
        >
          {o.render ?? o.label}
        </div>
      </span>
    );
  });
}

/** The touch sheet's rows: real buttons (a tap is the whole interaction), the caller's glyph-capable `render`,
 *  a check on the current value, group headings in place. Picking closes the sheet. */
function renderSheetOptions(
  visible: PickerOption[],
  ctx: { value: string; pick: (o: PickerOption) => void },
) {
  let lastGroup: string | undefined;
  return visible.map((o) => {
    const header =
      o.group !== lastGroup && o.group != null ? (
        <p key={`g-${o.group}`} className={styles.groupHeader} role="presentation">
          {o.group}
        </p>
      ) : null;
    lastGroup = o.group;
    const selected = o.value === ctx.value;
    return (
      <span key={o.value} className={styles.sheetItem}>
        {header}
        <button
          type="button"
          role="option"
          aria-selected={selected}
          disabled={o.disabled}
          className={styles.sheetOption}
          onClick={() => ctx.pick(o)}
        >
          <span className={styles.sheetOptionLabel}>{o.render ?? o.label}</span>
          {selected && <Check size={18} className={styles.sheetCheck} aria-hidden="true" />}
        </button>
      </span>
    );
  });
}
